import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Environment } from '../utils/Environment';
import './A3Builder.css';
import {
  SECTION_HEADINGS,
  EMPTY_STATE_MESSAGES,
  DEFAULT_VALUES,
  PREVIEW_SUBTITLE
} from './constants/A3Constants';
import {
  formatCurrency,
  getStatusClass
} from './utils/A3Utils';

interface A3DetailModel {
  a3Id: string;
  title: string;
  industrialEngineer: string;
  siteDc: string;
  region: string;
  status: string;
  date: string;
  validatedSavings: number;

  defineProblem?: {
    background: string;
    problemStatement: string;
  };

  currentState?: {
    description: string;
    metric1: string;
    metric2: string;
    metric1Unfavorable?: boolean;
    metric2Unfavorable?: boolean;
  };

  goalTargetState?: {
    goalStatement: string;
    targetMetric: string;
    targetDate: string;
  };

  causeAnalysis?: {
    cause: string;
    category: string;
    evidence: string;
  }[];

  countermeasures?: {
    countermeasure: string;
    linkedCause: string;
    owner: string;  
  }[];

  actionPlan?: {
    action: string;
    owner: string;
    dueDate: string;
    status: string;
  }[];

  resultsAndLessonsLearned?: {
    resultsSummary: string;
    lessonsLearned: {
      lesson: string;
      category: string;
    }[];
  };
}

const A3Detail: React.FC = () => {
  const { a3Id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [a3, setA3] = useState<A3DetailModel | null>(null);

  const loadA3 = useCallback(async () => {
    if (!a3Id) return;

    setLoading(true);

    try {
      const apiUrl = `${Environment.viewA3FormAPI()}?a3Id=${a3Id}`;
      const response = await axios.get<any>(apiUrl);
      const data = response.data;

      // Check if data has the new structured format or old sections array format
      let transformedData: A3DetailModel;

      if (data.sections && Array.isArray(data.sections)) {
        
        const findSection = (heading: string) => data.sections.find((s: any) => s.heading === heading);
        
        const problemSection = findSection('Define the Problem');
        const currentStateSection = findSection('Current State');
        const goalSection = findSection('Goal / Target State');
        const causeSection = findSection('Cause Analysis');
        const cmSection = findSection('Countermeasures');
        const actionSection = findSection('Action Plan');
        const resultsSection = findSection('Results & Lessons Learned');

        transformedData = {
          a3Id: data.a3Id || '',
          title: data.title || '',
          industrialEngineer: data.industrialEngineer || '',
          siteDc: data.siteDc || '',
          region: data.region || '',
          status: data.status || '',
          date: data.date || '',
          validatedSavings: data.validatedSavings || 0,
          
          defineProblem: {
            background: problemSection?.fields.find((f: any) => f.label === 'Background')?.value || '',
            problemStatement: problemSection?.fields.find((f: any) => f.label === 'Problem Statement')?.value || ''
          },
          
          currentState: {
            description: currentStateSection?.fields.find((f: any) => f.label === 'Description')?.value || '',
            metric1: currentStateSection?.fields.find((f: any) => f.label === 'Metric 1')?.value || '',
            metric2: currentStateSection?.fields.find((f: any) => f.label === 'Metric 2 (optional)')?.value || '',
            metric1Unfavorable: currentStateSection?.fields.find((f: any) => f.label === 'Metric 1 Unfavorable')?.value === 'true',
            metric2Unfavorable: currentStateSection?.fields.find((f: any) => f.label === 'Metric 2 Unfavorable')?.value === 'true'
          },
          
          goalTargetState: {
            goalStatement: goalSection?.fields.find((f: any) => f.label === 'Goal Statement')?.value || '',
            targetMetric: goalSection?.fields.find((f: any) => f.label === 'Target Metric')?.value || '',
            targetDate: goalSection?.fields.find((f: any) => f.label === 'Target Date')?.value || ''
          },
          
          causeAnalysis: (causeSection?.fields || [])
            .filter((f: any) => f.type === 'row')
            .map((row: any) => ({
              cause: row.children?.find((c: any) => c.label === 'Cause')?.value || '',
              category: row.children?.find((c: any) => c.label === 'Category')?.value || '',
              evidence: row.children?.find((c: any) => c.label === 'Evidence')?.value || ''
            })),
          
          countermeasures: (cmSection?.fields || [])
            .filter((f: any) => f.type === 'row')
            .map((row: any) => ({
              countermeasure: row.children?.find((c: any) => c.label === 'Countermeasure')?.value || '',
              linkedCause: row.children?.find((c: any) => c.label === 'Linked Cause')?.value || '',
              owner: row.children?.find((c: any) => c.label === 'Owner')?.value || ''
            })),
          
          actionPlan: (actionSection?.fields || [])
            .filter((f: any) => f.type === 'row')
            .map((row: any) => ({
              action: row.children?.find((c: any) => c.label === 'Action')?.value || '',
              owner: row.children?.find((c: any) => c.label === 'Owner')?.value || '',
              dueDate: row.children?.find((c: any) => /due/i.test(c.label))?.value || '',
              status: row.children?.find((c: any) => /status/i.test(c.label))?.value || ''
            })),
          
          resultsAndLessonsLearned: {
            resultsSummary: resultsSection?.content || '',
            lessonsLearned: (resultsSection?.fields || [])
              .filter((f: any) => f.type === 'row')
              .map((row: any) => ({
                lesson: row.children?.find((c: any) => c.label === 'Lesson')?.value || '',
                category: row.children?.find((c: any) => c.label === 'Category')?.value || ''
              }))
          }
        };
      } else {
        transformedData = data as A3DetailModel;
      }

      setA3(transformedData);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [a3Id]);

  useEffect(() => {
    loadA3();
  }, [loadA3]);

  if (loading) {
    return (
      <div className="a3-container">
        <div className="a3sheet">
          <div className="a3-loading-container">
            <div className="a3-spinner"></div>
            <div className="a3-loading-text">
              Loading A3 Form<span className="a3-loading-dots"></span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!a3) {
    return <div className="a3-container"><div className="a3sheet">A3 not found.</div></div>;
  }

  const causes = a3.causeAnalysis || [];
  const cms = a3.countermeasures || [];
  const actions = a3.actionPlan || [];
  const lessons = a3.resultsAndLessonsLearned?.lessonsLearned || [];
  const resultsSummary = a3.resultsAndLessonsLearned?.resultsSummary || '';

  return (
    <div className="a3-container">
      {/* ── Detail top bar ── */}
      <div className="a3-detail-topbar">
        <button
          className="a3-detail-back-btn"
          onClick={() => navigate('/wms-upload-ui/ie-hub/a3-repository')}
        >
          ← Back to A3 Repository
        </button>
        <button
          className="a3-detail-edit-btn"
          onClick={() => navigate(`/wms-upload-ui/ie-hub/a3-builder?a3Id=${a3Id}`)}
        >
          Edit this A3
        </button>
      </div>

      <div className="a3sheet" style={{ overflow: 'auto' }}>
        <div style={{ transform: 'scale(1)', transformOrigin: 'top left', width: '100%' }}>

          {/* Project Info inline header — matches builder preview exactly */}
          <div className="b-section">
            <div className="preview-inline-header" role="group" aria-label="Project info inline header">
              <div className="preview-inline-item title-only a3-text-left">
                <div className="preview-inline-value title-value a3-text-left">{a3.title || DEFAULT_VALUES.UNTITLED_A3}</div>
                <div className="a3-preview-subtitle">{PREVIEW_SUBTITLE}</div>
              </div>

              <div className="preview-inline-right">
                <div className="preview-inline-item">
                  <label>Industrial Engineer</label>
                  <div className="preview-inline-value">{a3.industrialEngineer || <span className="placeholder">{EMPTY_STATE_MESSAGES.EMPTY}</span>}</div>
                </div>

                <div className="preview-inline-item">
                  <label>Site / DC</label>
                  <div className="preview-inline-value">{a3.siteDc || <span className="placeholder">{EMPTY_STATE_MESSAGES.EMPTY}</span>}</div>
                </div>

                <div className="preview-inline-item">
                  <label>Date</label>
                  <div className="preview-inline-value">{a3.date || ''}</div>
                </div>

                <div className="preview-inline-item">
                  <label>Status</label>
                  <div className="preview-inline-value">
                    {a3.status
                      ? <span className={`status-badge ${getStatusClass(a3.status)}`}>{a3.status}</span>
                      : <span className="placeholder">{EMPTY_STATE_MESSAGES.EMPTY}</span>}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Two-column A3 grid — left: sections 1–3, right: sections 4–5 */}
          <div className="a3grid">
            <div className="left-col">

              {/* 1 Define the Problem */}
              <div className="box">
                <h3 className="section-heading-button"><span className="num">1</span>{SECTION_HEADINGS.DEFINE_PROBLEM}</h3>
                <div className="box-content">
                  {a3.defineProblem?.problemStatement || <span className="placeholder">{EMPTY_STATE_MESSAGES.EMPTY}</span>}
                </div>
                {a3.defineProblem?.background && (
                  <div className="box-content" style={{ marginTop: 8 }}>
                    <strong>Background:</strong> {a3.defineProblem.background}
                  </div>
                )}
              </div>

              {/* 2 Current State */}
              <div className="box">
                <h3 className="section-heading-button"><span className="num">2</span>{SECTION_HEADINGS.CURRENT_STATE}</h3>
                <div className="box-content">
                  {a3.currentState?.description || <span className="placeholder">{EMPTY_STATE_MESSAGES.EMPTY}</span>}
                </div>
                {(a3.currentState?.metric1 || a3.currentState?.metric2) && (
                  <div className="metric-strip" style={{ marginTop: 8 }}>
                    {a3.currentState.metric1 && (
                      <span className={`metric-chip${a3.currentState.metric1Unfavorable ? ' bad' : ''}`}>
                        <strong>{a3.currentState.metric1}</strong>
                      </span>
                    )}
                    {a3.currentState.metric2 && (
                      <span className={`metric-chip${a3.currentState.metric2Unfavorable ? ' bad' : ''}`}>
                        <strong>{a3.currentState.metric2}</strong>
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* 3 Goal / Target State */}
              <div className="box">
                <h3 className="section-heading-button"><span className="num">3</span>{SECTION_HEADINGS.GOAL_TARGET_STATE}</h3>
                <div className="box-content">
                  {a3.goalTargetState?.goalStatement || <span className="placeholder">{EMPTY_STATE_MESSAGES.EMPTY}</span>}
                </div>
                {(a3.goalTargetState?.targetMetric || a3.goalTargetState?.targetDate) && (
                  <div className="metric-strip" style={{ marginTop: 8 }}>
                    {a3.goalTargetState.targetMetric && <span className="metric-chip good"><strong>{a3.goalTargetState.targetMetric}</strong></span>}
                    {a3.goalTargetState.targetDate && <span className="metric-chip"><strong>{a3.goalTargetState.targetDate}</strong></span>}
                  </div>
                )}
              </div>

            </div>

            <div className="right-col">

              {/* 4 Cause Analysis */}
              <div className="box">
                <h3 className="section-heading-button"><span className="num">4</span>{SECTION_HEADINGS.CAUSE_ANALYSIS}</h3>
                {causes.length ? (
                  <table className="a3table">
                    <thead>
                      <tr><th>Cause</th><th>Category</th><th>Evidence</th></tr>
                    </thead>
                    <tbody>
                      {causes.map((c, i) => (
                        <tr key={i}>
                          <td>{c.cause}</td>
                          <td>{c.category}</td>
                          <td>{c.evidence}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : <p className="placeholder">{EMPTY_STATE_MESSAGES.NO_CAUSES_LOGGED}</p>}
              </div>

              {/* 5 Countermeasures */}
              <div className="box">
                <h3 className="section-heading-button"><span className="num">5</span>{SECTION_HEADINGS.COUNTERMEASURES}</h3>
                {cms.length ? (
                  <table className="a3table">
                    <thead>
                      <tr><th>Countermeasure</th><th>Linked Cause</th><th>Owner</th></tr>
                    </thead>
                    <tbody>
                      {cms.map((c, i) => (
                        <tr key={i}>
                          <td>{c.countermeasure}</td>
                          <td>{c.linkedCause}</td>
                          <td>{c.owner}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : <p className="placeholder">{EMPTY_STATE_MESSAGES.NO_COUNTERMEASURES_LOGGED}</p>}
              </div>

            </div>
          </div>

          {/* Full-width Action Plan (6) */}
          <div className="full-width-row" style={{ marginTop: 12 }}>
            <div className="box">
              <h3 className="section-heading-button"><span className="num">6</span>{SECTION_HEADINGS.ACTION_PLAN}</h3>
              {actions.length ? (
                <table className="a3table">
                  <colgroup>
                    <col style={{ width: '48%' }} />
                    <col style={{ width: '18%' }} />
                    <col style={{ width: '18%' }} />
                    <col style={{ width: '16%' }} />
                  </colgroup>
                  <thead>
                    <tr><th>Action</th><th>Owner</th><th>Due Date</th><th>Status</th></tr>
                  </thead>
                  <tbody>
                    {actions.map((a, i) => (
                      <tr key={i}>
                        <td>{a.action}</td>
                        <td>{a.owner}</td>
                        <td>{a.dueDate}</td>
                        <td><span className={`status-badge ${getStatusClass(a.status)}`}>{a.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <p className="placeholder">{EMPTY_STATE_MESSAGES.NO_ACTIONS_LOGGED}</p>}
            </div>
          </div>

          {/* Full-width Results & Lessons Learned (7) */}
          <div className="full-width-row" style={{ marginTop: 12 }}>
            <div className="box" style={{ display: 'flex', alignItems: 'flex-start', gap: 0, padding: 0, overflow: 'hidden' }}>
              {/* Left: heading + content + table */}
              <div className="results-box-content" style={{ padding: 16, flex: 1 }}>
                <h3 className="section-heading-button" style={{ margin: '0 0 10px 0' }}><span className="num">7</span>{SECTION_HEADINGS.RESULTS_LESSONS}</h3>
                <div className="box-content">{resultsSummary || <span className="placeholder">{EMPTY_STATE_MESSAGES.EMPTY}</span>}</div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginTop: 8 }}>
                  {/* Lessons table */}
                  <div style={{ flex: 1 }}>
                    {lessons.length > 0 && (
                      <table className="a3table">
                        <thead>
                          <tr><th>Lesson</th><th>Category</th></tr>
                        </thead>
                        <tbody>
                          {lessons.map((l, i) => (
                            <tr key={i}>
                              <td>{l.lesson}</td>
                              <td>{l.category}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                  {/* Right: savings card aligned with table - height based on number of rows */}
                  {(a3.validatedSavings !== undefined && a3.validatedSavings !== null) && (
                    <div style={{ flexShrink: 0, width: 200 }}>
                      <div className="savings-card-dark" style={{ 
                        height: lessons.length > 0 ? 'auto' : undefined,
                        minHeight: lessons.length > 0 ? `${40 + (lessons.length * 44)}px` : undefined 
                      }}>
                        <div className="scd-amt">{formatCurrency(a3.validatedSavings) || '$0'}</div>
                        <div className="scd-lbl">Annualized Savings</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default A3Detail;